<template>
  <ControlWrapper v-bind="controlWrapper" :styles="styles" :applied-options="appliedOptions" :error="control.errors">
    <SectionWrapper
      :title="controlOptions.title"
      :description="controlOptions.description"
      :title-class="styles.control.label"
    >
      <div class="mt-6 flex w-full flex-col gap-8">
        <div class="flex w-full flex-col gap-4">
          <WInput
            v-model="postcode"
            type="text"
            name="postcode"
            :label="controlOptions.properties?.postcode.title"
            subtitle
            :prefix="themeIcons.marker"
            @update:model-value="validatePostcode"
            :error="postcodeError"
          />

          <WTextarea
            v-model="note"
            name="note"
            :disabled="!control.enabled"
            :label="controlOptions.properties?.note.title"
          />
        </div>
      </div>
    </SectionWrapper>
  </ControlWrapper>
</template>

<script lang="ts">
  import { defineComponent, nextTick, ref, watch, onBeforeUnmount, computed } from 'vue';
  import { ControlElement } from '@jsonforms/core';
  import { rendererProps, useJsonFormsControl, RendererProps } from '@jsonforms/vue';
  import { useWidgetControl } from '../util';
  import { validatePostcode } from 'src/utils/helpers';
  import ControlWrapper from './ControlWrapper.vue';
  import SectionWrapper from '../../claims-widget/components/SectionWrapper.vue';
  import WInput from '../../base/Input.vue';
  import WTextarea from '../../base/Textarea.vue';
  import { config, themeIcons } from 'src/utils/config';
  import { useWidgetError } from '../util/composition';

  export interface PropertyValue {
    title: string;
    type: string;
  }

  export interface Property {
    postcode: PropertyValue;
    note: PropertyValue;
  }

  export interface RepairLocationConfig {
    required?: Array<keyof Property>;
    repairLocation: true;
    type: 'object';
    title: string;
    description: string;
    properties: Property;
  }

  export default defineComponent({
    components: {
      ControlWrapper,
      SectionWrapper,
      WInput,
      WTextarea,
    },
    props: {
      ...rendererProps<ControlElement>(),
    },
    setup(props: RendererProps<ControlElement>) {
      const { onValidate, errors } = useWidgetError();
      const control = useWidgetControl(useJsonFormsControl(props));
      const showErrors = ref(false);

      const controlData = computed<Partial<{ postcode: string; note: string }>>(() => control.control.value?.data);
      const controlOptions = computed(() => control.control.value?.schema as Partial<RepairLocationConfig>);
      const postcode = ref(controlData.value?.postcode || '');
      const note = ref(controlData.value?.note || '');
      const postcodeError = ref('');
      const listener = onValidate(() => {
        validatePostcodeAndSetError(postcode.value);
        showErrors.value = true;
      });
      onBeforeUnmount(listener.off);

      function validatePostcodeAndSetError(postcode?: string) {
        if (validatePostcode(postcode)) {
          postcodeError.value = '';
          errors.value['repairLocation'] = '';
          return;
        }
        postcodeError.value = 'Value must be a valid postcode';
        errors.value['repairLocation'] = 'Value must be a valid postcode';
      }

      watch(
        [note, postcode],
        () => {
          nextTick(() => {
            control.onChange({
              postcode: postcode.value,
              note: note.value,
            });
          });
        },
        { immediate: true }
      );

      return {
        note,
        postcode,
        postcodeError,
        clientConfig: config,
        controlOptions,
        showErrors,
        themeIcons,
        ...control,
        validatePostcode: validatePostcodeAndSetError,
      };
    },
  });
</script>
